import { useLocation } from 'react-router'

import { getPageKeyByPath } from '~/app/routes'
import { LocationDay } from '~/store/slices/types'
import { useStore } from '~/store/store'

export const supportedPageKeys = ['SURGEON_SCHEDULER', 'SURGERY_SCHEDULER', 'OPERATIONAL_PLANNER'] as const
export type SupportedPageKey = (typeof supportedPageKeys)[number]

export const isSupportedPageKey = (key: string | undefined): key is SupportedPageKey => key !== undefined && supportedPageKeys.includes(key as SupportedPageKey)
/**
 * Utility hook to manage drawer state across different pages
 */
export const usePageDrawer = () => {
    const { pathname } = useLocation()
    const currentPageKey = getPageKeyByPath(pathname)

    // Subscribe reactively so consumers re-render when the current page's drawer opens/closes.
    const isDrawerOpen = useStore(state => {
        switch (currentPageKey) {
            case 'SURGEON_SCHEDULER':
                return state.surgeonScheduler.isDrawerOpen
            case 'SURGERY_SCHEDULER':
                return state.surgeryScheduler.isDrawerOpen
            case 'OPERATIONAL_PLANNER':
                return state.operationalPlanner.isDrawerOpen
            default:
                return false
        }
    })

    const openPageDrawer = (
        {
            clearPreviousSelections,
            openDrawerArgs,
            pageKey,
            activeDrawerTab,
        }: { clearPreviousSelections?: boolean; openDrawerArgs?: LocationDay; pageKey?: SupportedPageKey; activeDrawerTab?: string } = {
            clearPreviousSelections: true,
        }
    ): boolean => {
        const state = useStore.getState()
        const drawerSlice = {
            SURGEON_SCHEDULER: state.surgeonScheduler,
            SURGERY_SCHEDULER: state.surgeryScheduler,
            OPERATIONAL_PLANNER: state.operationalPlanner,
        } satisfies Partial<Record<SupportedPageKey, unknown>>
        const resolvedPageKey = pageKey ?? currentPageKey
        const resolvedSlice = drawerSlice[resolvedPageKey as keyof typeof drawerSlice]
        if (clearPreviousSelections) {
            if (resolvedPageKey === 'SURGERY_SCHEDULER') {
                drawerSlice.SURGERY_SCHEDULER.actions.unselectDay()
            }
            if (resolvedPageKey === 'OPERATIONAL_PLANNER') {
                drawerSlice.OPERATIONAL_PLANNER.actions.unselectDay()
            }
        }
        const prevPageDrawerOpenState = resolvedSlice.isDrawerOpen
        resolvedSlice.actions.openDrawer(openDrawerArgs)

        if (activeDrawerTab && resolvedPageKey !== 'SURGEON_SCHEDULER') {
            resolvedSlice.actions.setActiveDrawerTab(activeDrawerTab as never)
        }

        return prevPageDrawerOpenState
    }

    const closePageDrawer = () => {
        const state = useStore.getState()
        const drawerSlice = {
            SURGEON_SCHEDULER: state.surgeonScheduler,
            SURGERY_SCHEDULER: state.surgeryScheduler,
            OPERATIONAL_PLANNER: state.operationalPlanner,
        } satisfies Partial<Record<SupportedPageKey, unknown>>
        const currentPageSlice = currentPageKey ? drawerSlice[currentPageKey as keyof typeof drawerSlice] : null
        currentPageSlice?.actions.closeDrawer()
    }

    return {
        page: currentPageKey,
        openPageDrawer,
        closePageDrawer,
        isDrawerOpen,
    } as const
}
